// n03 Tovus — peacock-motif emerald-night nikoh preview card.
//
// The full template is a zumrad-green (emerald) night invitation with gold
// filigree and a feathered peacock-frame centrepiece. The catalog card
// keeps that palette: a deep emerald-night ground inside a thin gold
// double frame with small corner diamonds, a tiny peacock-eye medallion
// (concentric peacock/gold/teal rings) above the couple's names set in a
// Parisienne-style script treatment, and a gold-sheen date line below.
import { type LayoutCtx } from "./_shared";

export function TovusPreviewLayout({ names, date, venue, accent, ink, bg }: LayoutCtx) {
  const peacock = "#1C6E8C";
  const teal = "#0E6B62";
  const gold = accent || "#C6A24A";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(120% 90% at 50% 0%, #163127 0%, ${bg} 55%, #081512 100%)`,
        padding: "6%",
        display: "flex",
      }}
    >
      {/* Thin gold double frame. */}
      <div
        style={{
          flex: 1,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${gold}99, inset 0 0 0 5px transparent, inset 0 0 0 6px ${gold}44`,
        }}
      >
        {/* Corner diamonds. */}
        {[
          { top: "3%", left: "3%" },
          { top: "3%", right: "3%" },
          { bottom: "3%", left: "3%" },
          { bottom: "3%", right: "3%" },
        ].map((pos, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              width: 7,
              height: 7,
              background: gold,
              transform: "rotate(45deg)",
              opacity: 0.85,
              ...pos,
            }}
          />
        ))}

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: gold,
            marginTop: "4%",
            marginBottom: "7%",
          }}
        >
          NIKOH TAKLIFNOMASI
        </div>

        {/* Peacock-eye medallion. */}
        <div
          style={{
            width: "26%",
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            marginBottom: "7%",
            background: `radial-gradient(circle at 50% 50%, ${gold} 0%, ${gold} 14%, ${peacock} 16%, ${peacock} 46%, ${teal} 48%, ${teal} 74%, ${gold} 76%, ${gold} 100%)`,
            boxShadow: `0 0 0 1px ${gold}66`,
          }}
        />

        <div
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.15,
            letterSpacing: "0.01em",
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Gold-sheen date line. */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.3em",
            marginTop: "4%",
            backgroundImage: `linear-gradient(90deg, ${gold}99 0%, #F3E3B0 45%, ${gold}99 100%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
